import { IPmFunn, IPmFunnActions, IPmFunnPatch, IPmFunnState } from './types'
import * as R from 'ramda'
import dayjs from 'dayjs'
import { ROOT_STORE_NAME } from '../../../store'
import { IStoreState } from '../../../../../reducers/types'
import { commonRedux } from '../../../../../common/redux'

export const STORE_NAME = '@tilsyn/pmFunn'

const { fetchActions, fetchActionTypes, fetchWithIndexHandlers } =
  commonRedux.fetchWithIndexHelpers<IPmFunn, typeof STORE_NAME>(
    STORE_NAME,
    'funnId'
  )

export const pmFunnActionsTypes = {
  ...fetchActionTypes,
  POST: `${STORE_NAME}/POST` as const,
  POST_FAIL: `${STORE_NAME}/POST_FAIL` as const,
  POST_OK: `${STORE_NAME}/POST_OK` as const,
  PATCH_CURRENT_FUNN: `${STORE_NAME}/PATCH_CURRENT_FUNN` as const,
  PATCH_CURRENT_FUNN_FAIL: `${STORE_NAME}/PATCH_CURRENT_FUNN_FAIL` as const,
  PATCH_CURRENT_FUNN_OK: `${STORE_NAME}/PATCH_CURRENT_FUNN_OK` as const,
  DELETE: `${STORE_NAME}/DELETE` as const,
  DELETE_IF_EMPTY: `${STORE_NAME}/DELETE_IF_EMPTY` as const,
  DELETE_FAIL: `${STORE_NAME}/DELETE_FAIL` as const,
  DELETE_OK: `${STORE_NAME}/DELETE_OK` as const,
  DELETE_AM_KOBLING: `${STORE_NAME}/DELETE_AM_KOBLING` as const,
  DELETE_AM_KOBLING_FAIL: `${STORE_NAME}/DELETE_AM_KOBLING_FAIL` as const,
  DELETE_AM_KOBLING_OK: `${STORE_NAME}/DELETE_AM_KOBLING_OK` as const,
  RESET: `${STORE_NAME}/RESET` as const,
  RESET_CURRENT_FUNN_STATUS: `${STORE_NAME}/RESET_CURRENT_FUNN_STATUS` as const,
  SET_SLAKTEDATO_FROM: `${STORE_NAME}/SET_SLAKTEDATO_FROM` as const,
}

export const pmFunnActions = {
  ...fetchActions,
  post: (
    funn: Partial<IPmFunnPatch>,
    shouldPushToFunnRoute?: boolean,
    initialObservasjonImageIds?: string[]
  ) => ({
    type: pmFunnActionsTypes.POST,
    funn,
    initialObservasjonImageIds,
    shouldPushToFunnRoute,
  }),
  postFail: (error: string) => ({
    type: pmFunnActionsTypes.POST_FAIL,
    error,
  }),
  postOk: (id: string) => ({
    type: pmFunnActionsTypes.POST_OK,
    id,
  }),
  patchCurrentFunn: (
    funnPatch: Partial<IPmFunnPatch>,
    shouldFetchNew = true
  ) => ({
    type: pmFunnActionsTypes.PATCH_CURRENT_FUNN,
    funnPatch,
    shouldFetchNew,
  }),
  patchCurrentFunnFail: (error: string) => ({
    type: pmFunnActionsTypes.PATCH_CURRENT_FUNN_FAIL,
    error,
  }),
  patchCurrentFunnOk: () => ({
    type: pmFunnActionsTypes.PATCH_CURRENT_FUNN_OK,
  }),
  delete: (funnId: string, silent = false) => ({
    type: pmFunnActionsTypes.DELETE,
    funnId,
    silent,
  }),
  deleteIfEmpty: (funnId: string, silent = true) => ({
    type: pmFunnActionsTypes.DELETE_IF_EMPTY,
    funnId,
    silent,
  }),
  deleteFail: (error: string) => ({
    type: pmFunnActionsTypes.DELETE_FAIL,
    error,
  }),
  deleteOk: (funnId: string) => ({
    type: pmFunnActionsTypes.DELETE_OK,
    funnId,
  }),
  deleteAmKobling: (funnId: string, tilknyttetAmFunnId: string) => ({
    type: pmFunnActionsTypes.DELETE_AM_KOBLING,
    funnId,
    tilknyttetAmFunnId,
  }),
  deleteAmKoblingFail: (error: string) => ({
    type: pmFunnActionsTypes.DELETE_AM_KOBLING_FAIL,
    error,
  }),
  deleteAmKoblingOk: () => ({
    type: pmFunnActionsTypes.DELETE_AM_KOBLING_OK,
  }),
  resetCurrentFunnStatus: (id?: string) => ({
    type: pmFunnActionsTypes.RESET_CURRENT_FUNN_STATUS,
    id,
  }),
  reset: () => ({
    type: pmFunnActionsTypes.RESET,
  }),
  fetchList: (slakteDatoFrom?: string, silent = false) => ({
    type: pmFunnActionsTypes.FETCH_LIST,
    slakteDatoFrom,
    silent,
  }),
  setSlakteDatoFrom: (slakteDatoFrom: string) => ({
    type: pmFunnActionsTypes.SET_SLAKTEDATO_FROM,
    slakteDatoFrom,
  }),
}

// selectors
export const pmFunnSelectors = {
  getLoadingStatus: (
    state: IStoreState
  ): { loading: boolean; loaded: boolean; error: string } =>
    R.pick(['loading', 'loaded', 'error'], state[ROOT_STORE_NAME][STORE_NAME]),

  getById: (id: string) => (state: IStoreState) =>
    state[ROOT_STORE_NAME][STORE_NAME].data[id],

  currentFunnStatus: (state: IStoreState) =>
    state[STORE_NAME].currentFunnStatus,

  getFunnList: (state: IStoreState) =>
    R.pipe(
      R.defaultTo({}),
      R.pluck('data'),
      R.values
    )(state[ROOT_STORE_NAME][STORE_NAME].data),

  getSlakteDatoFrom: (state: IStoreState): string =>
    state[ROOT_STORE_NAME][STORE_NAME].slakteDatoFrom,
}

export const initialState: IPmFunnState = {
  ...commonRedux.initialListState,
  currentFunnStatus: {
    error: '',
    saved: true,
    saving: false,
  },
  slakteDatoFrom: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
}

const setCurrentFunnStatus =
  (
    error: string,
    saved: boolean,
    saving: boolean,
    creating?: boolean,
    id?: string
  ) =>
  (state: IPmFunnState): IPmFunnState => ({
    ...state,
    currentFunnStatus: {
      error: error,
      saved: saved,
      saving: saving,
      creating: creating ?? state.currentFunnStatus.creating,
      id: id ?? state.currentFunnStatus.id,
    },
  })

const customReducer = commonRedux.createReducer<IPmFunnState, IPmFunnActions>(
  initialState,
  {
    ...fetchWithIndexHandlers,
    [pmFunnActionsTypes.DELETE]: (state, { funnId }): IPmFunnState => ({
      ...state,
      data: R.omit([funnId])(state.data),
      index: state.index.filter((i) => i !== funnId),
    }),

    [pmFunnActionsTypes.RESET_CURRENT_FUNN_STATUS]: (
      state,
      { id }
    ): IPmFunnState =>
      setCurrentFunnStatus('', true, false, false, id || '')(state),

    [pmFunnActionsTypes.RESET]: (): IPmFunnState => initialState,

    [pmFunnActionsTypes.POST]: (state): IPmFunnState =>
      setCurrentFunnStatus('', false, true, true)(state),

    [pmFunnActionsTypes.POST_OK]: (state, { id }): IPmFunnState =>
      setCurrentFunnStatus('', true, false, false, id)(state),

    [pmFunnActionsTypes.POST_FAIL]: (state, { error }): IPmFunnState =>
      setCurrentFunnStatus(error, false, false, false, undefined)(state),

    [pmFunnActionsTypes.PATCH_CURRENT_FUNN]: (state): IPmFunnState =>
      setCurrentFunnStatus('', false, true)(state),

    [pmFunnActionsTypes.PATCH_CURRENT_FUNN_OK]: (state): IPmFunnState =>
      setCurrentFunnStatus('', true, false)(state),

    [pmFunnActionsTypes.PATCH_CURRENT_FUNN_FAIL]: (
      state,
      { error }
    ): IPmFunnState => setCurrentFunnStatus(error, false, false)(state),

    [pmFunnActionsTypes.SET_SLAKTEDATO_FROM]: (
      state,
      { slakteDatoFrom }
    ): IPmFunnState => ({
      ...state,
      slakteDatoFrom,
    }),
  }
)

export const pmFunnReducer = {
  [STORE_NAME]: customReducer,
}
